/**
 * Como os números das abas se escrevem (11.10). **Nenhuma conta**: o serviço entrega frações,
 * reais e dias já calculados (AD-14, AD-29), e aqui só se formata — `"0.6500"` vira `"65,0%"`.
 *
 * Texto que não for número volta **verbatim**: um `NaN%` na tela pareceria defeito de cálculo,
 * quando o que houve foi o contrato mudar de forma.
 */

import { ErroDaApi, MENSAGEM_DE_ERRO_DESCONHECIDO } from '@/api/erros'
import { DATA, MOEDA_EXATA, NUMERO, dataPuraPorExtenso } from '@/modulos/negocios/apresentacao'

/** O travessão de "não há o que medir". */
export const SEM_MEDIDA = '—'

const PERCENTUAL = new Intl.NumberFormat('pt-BR', {
  style: 'percent',
  minimumFractionDigits: 1,
  maximumFractionDigits: 1,
})

/** `"0.4130"` -> `"41,3%"`; nulo vira o travessão. */
export function percentual(fracao: string | null | undefined): string {
  if (fracao === null || fracao === undefined) return SEM_MEDIDA
  const numero = Number(fracao)
  return Number.isFinite(numero) ? PERCENTUAL.format(numero) : fracao
}

/** `"206150.00"` -> `"R$ 206.150,00"`; nulo vira o travessão. */
export function dinheiro(valor: string | null | undefined): string {
  if (valor === null || valor === undefined) return SEM_MEDIDA
  const numero = Number(valor)
  return Number.isFinite(numero) ? MOEDA_EXATA.format(numero) : valor
}

/** `"6.10"` -> `"6,1"`; nulo vira o travessão. */
export function dias(valor: string | null | undefined): string {
  if (valor === null || valor === undefined) return SEM_MEDIDA
  const numero = Number(valor)
  return Number.isFinite(numero) ? NUMERO.format(numero) : valor
}

/** Quantidade decimal (`"3.5000"` -> `"3,5"`, 11.11: produtos vendidos); nulo vira o travessão. */
export function quantidade(valor: string | null | undefined): string {
  return dias(valor)
}

/** Contagem inteira em pt-BR; nulo vira o travessão. */
export function contagem(valor: number | null | undefined): string {
  return valor === null || valor === undefined ? SEM_MEDIDA : NUMERO.format(valor)
}

/** Um instante UTC na data de São Paulo; nulo vira o travessão. */
export function instantePorExtenso(instante: string | null | undefined): string {
  if (instante === null || instante === undefined) return SEM_MEDIDA
  const quando = new Date(instante)
  return Number.isNaN(quando.getTime()) ? instante : DATA.format(quando)
}

/** O período aplicado: `01/08/2026 a 29/08/2026`. */
export function periodoPorExtenso(periodo: { inicio: string; fim: string }): string {
  return `${dataPuraPorExtenso(periodo.inicio)} a ${dataPuraPorExtenso(periodo.fim)}`
}

/** A mensagem do serviço, verbatim — o SPA nunca reescreve mensagem do backend. */
export function mensagemDe(erro: unknown): string {
  return erro instanceof ErroDaApi ? erro.message : MENSAGEM_DE_ERRO_DESCONHECIDO
}

/** Para gráfico: o texto do contrato como número (o gráfico só **desenha** o que veio). */
export function paraGrafico(valor: string | null | undefined): number {
  const numero = Number(valor ?? 0)
  return Number.isFinite(numero) ? numero : 0
}
